import { floorKey } from "@cervval.js/common/src/tools/collection.tools";
import { asDate, toTimestamp, TsDate, TsParam, UnixTimestamp, valueOf } from "@cervval.js/common/src/tools/timestamps.tools";
import { CalendarMode, Second, StartDurationFinish, toSecond, WorkCalendar, WorkCalendarPartition, WorkDateType } from "./calendars.definition";
import { buildCalendarCursor, buildTimeline, runCalendarCursor } from "./calendars.tools";

export interface StartAndDuration {
    start: TsParam;
    duration: Second;
}
export interface FinishAndDuration {
    finish: TsParam;
    duration: Second;
}
export interface StartAndFinish {
    start: TsParam;
    finish: TsParam;
}

// TODO BGS passer en TsParam
/**
 * WorkCalendar Helper
 */
export class WorkCalendarHelper {
    private _calendar: WorkCalendar;
    private _timeline: Map<UnixTimestamp | null, WorkCalendarPartition>;
    private _ticks: number[];

    private _mode: CalendarMode = CalendarMode.WRAP; // ! SLOT calendars is not implemented yet

    constructor(calendar: WorkCalendar) {
        this._calendar = calendar;

        this._timeline = buildTimeline(this._calendar);
        this._ticks = Array.from(this._timeline.keys()).map(v => { return v === null ? null : valueOf(v); }).filter(v => v !== null) as number[];
    }

    /** Return corresponding calendar Id */
    public get id(): number | undefined {
        return this._calendar.id;
    }

    /** Return the from date adjusted to a workable date */
    public getWorkableDate(from: TsParam, dateType: WorkDateType): TsDate {
        const cursor = buildCalendarCursor(this, from, { duration: toSecond(0) }, dateType);
        runCalendarCursor(cursor);
        return cursor.position;
    }

    /**
     * Give start and duration, get finish
     * @param input finish is updated in reference
     * @returns finish
     */
    public getFinish(input: StartAndDuration): TsDate {
        const cursor = buildCalendarCursor(this, input.start, { duration: input.duration }, WorkDateType.FINISH);
        runCalendarCursor(cursor);
        return cursor.position;
    }

    /**
     * Give finish and duration, get start
     * @returns start
     */
    public getStart(input: FinishAndDuration): TsDate {
        const cursor = buildCalendarCursor(this, input.finish, { duration: toSecond(- input.duration) }, WorkDateType.START);
        runCalendarCursor(cursor);
        return cursor.position;
    }

    /**
     * Give start and finish, get duration
     * @returns duration in Second
     */
    public getDuration(input: StartAndFinish): Second {
        const cursor = buildCalendarCursor(this, input.start, { date: asDate(input.finish) }, WorkDateType.FINISH);
        runCalendarCursor(cursor);
        return toSecond(cursor.result.elapsed);
    }

    /** Return the partition corresponding to the time (at) requested */
    public getPartitionAt(at: UnixTimestamp): WorkCalendarPartition {
        const partitionTick = floorKey(this._ticks, at);
        const partition = this._timeline.get(partitionTick === null ? null : toTimestamp(partitionTick));
        if (partition === undefined) { throw new Error("No reachable partition on timeline. This should not happen"); }
        return partition;
    }

    /**
     * return the worked seconds between 2 dates
     */
    public getWorkedSecondsBetween(from: TsParam, to: TsParam): Second {
        return this.getDuration({ start: from, finish: to });
    }

    /**
     * add worked seconds to a date and return a start or finish date
     */
    public addWorkedSeconds(from: TsParam, seconds: Second, targetDateType: WorkDateType): TsDate {
        const cursor = buildCalendarCursor(this, from, { duration: seconds }, WorkDateType.FINISH);
        runCalendarCursor(cursor);
        return cursor.position;
    }

    /** Proxy function that complete the start, finish, duration object (SFD) according to the calendar - return undefined if the SFD object cannot be fulfilled */
    public process(p: Partial<StartDurationFinish>): StartDurationFinish | undefined {
        if (p.start != null && p.finish != null) {
            return {
                start: p.start,
                duration: this.getDuration({ start: p.start, finish: p.finish }),
                finish: p.finish
            };
        } else if (p.start != null && p.duration != null) {
            if (this._mode === CalendarMode.WRAP) {
                const finish = this.getFinish({ start: p.start, duration: p.duration });
                return {
                    start: this.getWorkableDate(p.start, WorkDateType.START),
                    duration: p.duration,
                    finish
                };
            } else if (this._mode === CalendarMode.SLOT) {
                return undefined; // ! not implemented
            }
        } else if (p.finish != null && p.duration != null) {
            if (this._mode === CalendarMode.WRAP) {
                const start = this.getStart({ finish: p.finish, duration: p.duration });
                return {
                    start,
                    duration: p.duration,
                    finish: this.getWorkableDate(p.finish, WorkDateType.FINISH)
                };
            } else if (this._mode === CalendarMode.SLOT) {
                return undefined; // ! not implemented
            }
        }
        return undefined;
    }

}